import { Node } from '@tiptap/core'
import { toSafeEmbedSrc } from './embed-click-to-load'

export interface BookmarkAttrs {
  href: string
  title?: string
  description?: string
  image?: string
  favicon?: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    bookmark: {
      /**
       * Replace the selection with a bookmark card
       */
      setBookmark: (attrs: BookmarkAttrs) => ReturnType
    }
  }
}

const CLASS = 'notch-bookmark'
const MAX_TITLE = 200
const MAX_DESCRIPTION = 300
const part = (name: string) => `${CLASS}__${name}`

/** Where a bookmark may lead: http or https, as the browser parses it. Bind the returned URL, not the input. */
function toSafeLinkHref(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url.trim())
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null
  } catch {
    return null
  }
}

function withHttps(url: string): string {
  const trimmed = url.trim()
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/\//, '')}`
}

function hostOf(href: string): string {
  try {
    return new URL(href).hostname
  } catch {
    return href
  }
}

const clipped = (value: unknown, max: number): string => (typeof value === 'string' ? value.trim().slice(0, max) : '')

const firstSafeImage = (candidates: Array<{ href?: string }> | undefined): string | undefined => {
  for (const candidate of candidates ?? []) {
    const safe = toSafeEmbedSrc(candidate?.href)
    if (safe) return safe
  }
  return undefined
}

/**
 * Card attributes from the iframe.ly response of `POST /utilities/analyze-embed-url`. A failed lookup or a page
 * without metadata still gives a card: the host as its title, as the bookmark element does. Null when `url` is not
 * an http(s) URL.
 */
export function bookmarkAttrsFromPreview(url: string, data: any): BookmarkAttrs | null {
  const href = toSafeLinkHref(withHttps(url))
  // Text that is no URL has nothing to open: it stays text
  if (!href) return null
  const preview = data && !data.error ? data : undefined
  const title = clipped(preview?.meta?.title, MAX_TITLE)
  const description = clipped(preview?.meta?.description, MAX_DESCRIPTION)
  const image = firstSafeImage(preview?.links?.thumbnail)
  const favicon = firstSafeImage(preview?.links?.icon)
  return {
    href,
    title: title || hostOf(href),
    ...(description && { description }),
    ...(image && { image }),
    ...(favicon && { favicon })
  }
}

const textOf = (el: Element, name: string): string | null =>
  el.querySelector(`.${part(name)}`)?.textContent?.trim() || null

const srcOf = (el: Element, name: string): string | null =>
  toSafeEmbedSrc(el.querySelector(`.${part(name)}`)?.getAttribute('src'))

/**
 * A link preview card inside a table cell. Stored as `div > a > img, span, span, span, img` carrying only classes,
 * href, target, rel and src: the backend's rich-text sanitizer strips data attributes and unknown tags, so the card
 * has to read back from what it keeps.
 */
export default Node.create({
  name: 'bookmark',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      href: { default: null, rendered: false },
      title: { default: null, rendered: false },
      description: { default: null, rendered: false },
      image: { default: null, rendered: false },
      favicon: { default: null, rendered: false }
    }
  },

  parseHTML() {
    return [
      {
        tag: `div.${CLASS}`,
        getAttrs: element => {
          const el = element as HTMLElement
          const href = toSafeLinkHref(el.querySelector(`a.${part('link')}`)?.getAttribute('href'))
          // Nothing to open: not a card, so the text falls back to plain content
          if (!href) return false
          return {
            href,
            title: textOf(el, 'title'),
            description: textOf(el, 'description'),
            image: srcOf(el, 'image'),
            favicon: srcOf(el, 'favicon')
          }
        }
      }
    ]
  },

  renderHTML({ node }) {
    const { href, title, description, image, favicon } = node.attrs
    const safeHref = toSafeLinkHref(href)
    const safeImage = toSafeEmbedSrc(image)
    const safeFavicon = toSafeEmbedSrc(favicon)
    const host = safeHref ? hostOf(safeHref) : ''
    const children = [
      safeFavicon && ['img', { class: part('favicon'), src: safeFavicon, alt: '' }],
      ['span', { class: part('title') }, title || host],
      description && ['span', { class: part('description') }, description],
      ['span', { class: part('url') }, host],
      safeImage && ['img', { class: part('image'), src: safeImage, alt: '' }]
    ].filter(Boolean)
    // Without a safe URL there is nothing to open: the text stays, the link goes
    const inner = safeHref
      ? ['a', { class: part('link'), href: safeHref, target: '_blank', rel: 'noopener noreferrer' }, ...children]
      : ['span', { class: part('link') }, ...children]
    return ['div', { class: CLASS }, inner] as any
  },

  addCommands() {
    return {
      setBookmark:
        (attrs: BookmarkAttrs) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            const { selection } = tr
            tr.replaceRangeWith(selection.from, selection.to, this.type.create(attrs))
          }
          return true
        }
    }
  }
})
